<template>
  <div class="workspace-preview w-100 h-100 px-6">
    <v-skeleton-loader
      v-if="workspace.loading"
      class="h-100 bg-transparent"
      type="actions, paragraph, list-item, paragraph@4"
    />

    <div class="pb-6" v-else-if="workspace.data">
      <header
        class="workspace-preview__header d-flex align-center justify-space-between ga-4 pt-6 pb-3"
      >
        <div class="d-flex align-center ga-4">
          <!-- With the tree hidden there is nothing left in the panel to expand it from, so the
               control moves in here, ahead of the workspace. -->
          <v-btn
            v-if="treeCollapsed"
            v-tooltip:top="{ text: $t('mspWorkspaceTreeview.expandPanel'), openDelay: 300 }"
            icon="$showNavigationXs"
            density="compact"
            :aria-label="$t('mspWorkspaceTreeview.expandPanel')"
            data-testid="expand-tree-btn"
            @click="$emit('toggle-collapsed')"
          ></v-btn>
          <div
            class="d-flex"
            :class="{ 'branded-workspace-icon': !!workspace.data?.branding?.brandColor }"
            :style="{ 'background-color': workspace.data?.branding?.brandColor }"
          >
            <img
              alt="Logo"
              v-if="workspace.data?.branding?.headerIconUrl"
              :src="workspace.data?.branding.headerIconUrl"
              height="40"
            />
            <v-icon v-else :icon="iconsMap[workspace.data.type]" size="40" />
          </div>
          <div>
            <div class="headline6" data-testid="workspace-preview-title">
              {{ workspaceName }}
            </div>
            <div class="text-indigo-medium body2">
              {{ workspace.data.workspaceId }}
            </div>
          </div>
        </div>

        <div class="workspace-preview__actions">
          <msp-workspace-actions-menu
            :item="workspace.data"
            :item-actions="itemActions"
            :context="ManageWorkspacesContext.SINGLE_HIERARCHY"
            :workspace-store="workspaceStore"
            :disabled-actions-map="disabledActionsMap"
            action-test-id-prefix="workspace-preview"
          >
            <template #activator="{ props }">
              <v-btn rounded="xl" color="primary" v-bind="props">
                {{ $t("general.actions") }}
                <v-icon icon="$triangle" class="ml-2 mt-1" />
              </v-btn>
            </template>
          </msp-workspace-actions-menu>

          <v-btn
            rounded="xl"
            color="primary"
            variant="outlined"
            @click="onOpenWorkspaceClick"
            v-if="showViewWorkspace"
            data-testid="msp-workspace-preview-view-workspace-btn"
          >
            {{ $t("mspWorkspacePreview.viewWorkspace") }}
            <v-icon icon="$chevronRight" size="14" class="ml-1" />
          </v-btn>
        </div>
      </header>

      <div class="workspace-preview__meta">
        <div class="d-flex flex-column ga-2">
          <div class="subtitle2">
            {{ $t("mspWorkspacePreview.labels") }}
          </div>
          <div class="d-flex flex-wrap ga-2" data-testid="workspace-preview-labels">
            <template v-if="workspace.data.labels.length">
              <v-chip
                v-for="label in workspace.data.labels"
                variant="flat"
                :color="label.color"
                :text="label.name"
                :key="label.id"
                label
                rounded="3"
                density="compact"
                class="px-2"
              >
                <span class="coro-label text-uppercase text-primary">
                  {{ label.name }}
                </span>
              </v-chip>
            </template>
            <span v-else class="body3 text-indigo-medium">
              {{ $t("mspWorkspacePreview.noLabelsAssigned") }}
            </span>
          </div>
        </div>
        <div class="d-flex flex-column ga-2">
          <div class="subtitle2">
            {{ $t("mspWorkspacePreview.globalPolicy") }}
          </div>
          <div v-if="workspace.data.mspPolicyName" class="body2">
            {{ workspace.data.mspPolicyName }}
          </div>
          <div v-else class="body3 text-indigo-medium">
            {{ $t("mspWorkspacePreview.noPolicyAssigned") }}
          </div>
        </div>
      </div>

      <v-divider color="indigo-faint" opacity="100" class="my-6" />

      <section>
        <div class="subtitle1 mb-4">
          {{ $t("mspWorkspacePreview.subscriptionDetails") }}
        </div>
        <div class="workspace-preview__subscription">
          <div>
            <div class="subtitle2 mb-2">
              {{ $t("mspWorkspacePreview.status") }}
            </div>
            <div class="body2">
              {{ $t(`subscriptions.plans.${workspace.data.subscriptionType}`) }}
            </div>
            <div v-if="subscriptionDate" class="body2 text-indigo-medium">
              {{ subscriptionDate }}
            </div>
            <template v-if="showTrialRemainingTime">
              <div
                v-if="isTrialExpired"
                class="body2 text-orange-base"
                v-text="$t('mspWorkspacePreview.trialPeriodExpired')"
              ></div>
              <div
                v-else
                class="body2 text-indigo-medium"
                :class="{ 'text-orange-base': trialEndsNearExpiration }"
              >
                {{
                  $t("manageWorkspaces.table.expiresIn", {
                    n: getTimeUntil(workspaceData?.trialEndDate),
                  })
                }}
              </div>
            </template>
          </div>

          <div>
            <div class="subtitle2 mb-2">
              {{ $t("mspWorkspacePreview.plan") }}
            </div>
            <div>
              <span class="body2"> {{ $t("mspWorkspacePreview.bundle") }}: </span>
              <span class="subtitle2">
                {{ formattedBundles }}
              </span>
            </div>
            <div>
              <span class="body2"> {{ $t("mspWorkspacePreview.modules") }}: </span>
              <span class="subtitle2">
                {{ formattedModules }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <v-divider color="indigo-faint" opacity="100" class="my-6" />

      <section>
        <div class="subtitle1 mb-4">
          {{ $t("mspWorkspacePreview.workspaceStatistics") }}
        </div>
        <div class="d-flex flex-wrap">
          <div v-for="stat in stats" :key="stat.key" class="v-col-6 pa-1">
            <div
              class="workspace-preview__stat-card h-100 d-flex align-center justify-space-between"
              :class="{ 'workspace-preview__stat-card--hoverable': stat.callback }"
              :data-testid="`workspace-preview-stat-card-${stat.key}`"
              @click="stat.callback?.()"
            >
              <div class="d-flex flex-column">
                <div class="d-flex align-center mb-1">
                  <v-icon :icon="stat.icon" size="48" class="mr-2" />
                  <div class="workspace-preview__stat-value">{{ stat.value }}</div>
                </div>

                <div class="subtitle2">
                  {{ stat.label }}
                </div>
              </div>

              <v-icon v-if="stat.callback" icon="$chevronRight" size="18"></v-icon>
            </div>
          </div>
        </div>
      </section>

      <template v-if="workspaceData?.type === WorkspaceType.CHANNEL">
        <cui-bundle-breakdown-widget
          class="mt-6"
          :rows="bundleStats.data ?? []"
          :loading="bundleStats.loading"
          :error="!!bundleStats.error"
          :title="$t('mspWorkspacePreview.bundleBreakdown.title')"
          :bundle-labels="bundleLabels"
          :column-labels="columnLabels"
          :columns="channelColumns"
          :error-label="$t('mspWorkspacePreview.bundleBreakdown.errorLabel')"
          :retry-label="$t('general.retry')"
          :interactive="false"
          :other-label="$t('mspWorkspacePreview.bundleBreakdown.otherLabel')"
          :merged-bundles-label="
            (n) => $t('mspWorkspacePreview.bundleBreakdown.mergedBundlesLabel', { n })
          "
          :view-all-label="$t('mspWorkspacePreview.bundleBreakdown.viewAllLabel')"
          :view-less-label="$t('mspWorkspacePreview.bundleBreakdown.viewLessLabel')"
          @retry="$emit('retry-fetch-bundle-stats')"
        />

        <msp-billable-usage-widget class="mt-6" :workspace-id="workspaceData.workspaceId" />

        <msp-workspace-preview-child-workspaces-table
          class="mt-4"
          :child-workspaces="childWorkspacesData"
          @load-more-child-workspaces="$emit('load-more-child-workspaces', $event)"
          @view-action-clicked="handleChildWorkspaceAction"
        ></msp-workspace-preview-child-workspaces-table>
      </template>
    </div>
    <div
      v-else-if="workspace.error"
      class="workspace-preview__error h-100 w-100 d-flex align-center justify-center flex-column"
      data-testid="msp-workspace-preview-error-state"
    >
      <v-icon icon="$alertTriangle" size="80"></v-icon>
      <div class="headline6 mb-1 text-center">
        {{ $t("mspWorkspacePreview.errorTitle") }}
      </div>
      <span class="body2 mb-4 text-center text-indigo-medium">
        {{ $t("mspWorkspacePreview.errorSubtitle") }}
      </span>
      <div class="body2 coro-link" @click="$emit('retry-fetch-details')">
        {{ $t("general.retry") }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { MspWorkspaceDetails, MspWorkspaceListItem } from "@/_store/msp/msp.module.ts";
import { getEnabledModules } from "@coronet/cui";
import { getTimeUntil, getFormattedDateTime } from "@/_helpers/date";
import { trackGtmEvent } from "@/_helpers/analytics";
import {
  MspWorkspaceAction,
  SubscriptionBundle,
  SubscriptionModule,
  SubscriptionType,
  WorkspaceType,
} from "@/constants/workplaces.ts";
import { useI18n } from "vue-i18n";
import moment from "moment";
import { ManageWorkspacesContext } from "@/constants/msp.ts";
import {
  buildSingleHierarchyDisabledMap,
  handleMspWorkspaceViewAction,
  type WorkspaceActionsStore,
} from "@/_helpers/msp-actions.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import MspWorkspaceActionsMenu from "@/components/msp/MspWorkspaceActionsMenu.vue";
import MspWorkspacePreviewChildWorkspacesTable from "@/components/msp/MspWorkspacePreviewChildWorkspacesTable.vue";
import MspBillableUsageWidget from "@/components/msp/MspBillableUsageWidget.vue";
import type { DataValue, Paged } from "@/types.ts";
import { isCanadaEnv } from "@/constants/general.ts";
import startCase from "lodash/startCase";
import {
  type BundleBreakdownRow,
  CuiBundleBreakdownWidget,
  WorkspaceBreakdownColumn,
} from "@coronet/cui";

type WorkspaceStat = {
  key: string;
  label: string;
  value: string;
  icon: string;
  callback?: () => void;
};

const formatNumber = (value: number) => {
  return new Intl.NumberFormat().format(value);
};

const iconsMap: Record<WorkspaceType, string> = {
  [WorkspaceType.REGULAR]: "icon-regular-plan-s",
  [WorkspaceType.CHANNEL]: "icon-parent-plan-s",
  [WorkspaceType.CHILD]: "icon-child-plan-s",
};

const channelColumns = [
  WorkspaceBreakdownColumn.TOTAL,
  WorkspaceBreakdownColumn.CHANNEL,
  WorkspaceBreakdownColumn.CHILD,
];

export default defineComponent({
  components: {
    MspWorkspacePreviewChildWorkspacesTable,
    MspWorkspaceActionsMenu,
    CuiBundleBreakdownWidget,
    MspBillableUsageWidget,
  },
  props: {
    workspace: {
      type: Object as PropType<DataValue<MspWorkspaceDetails>>,
      required: true,
    },
    itemActions: {
      type: Array as PropType<MspWorkspaceAction[]>,
      default: () => [],
    },
    workspaceStore: {
      type: Object as PropType<WorkspaceActionsStore>,
      required: true,
    },
    treeCollapsed: {
      type: Boolean,
      default: false,
    },
    childWorkspacesData: {
      type: Object as PropType<DataValue<Paged<MspWorkspaceListItem>>>,
      required: true,
    },
    bundleStats: {
      type: Object as PropType<DataValue<BundleBreakdownRow[]>>,
      required: true,
    },
  },
  emits: [
    "retry-fetch-details",
    "retry-fetch-bundle-stats",
    "load-more-child-workspaces",
    "toggle-collapsed",
  ],
  setup(props) {
    const { t } = useI18n();

    const workspaceData = computed<MspWorkspaceDetails | undefined>(() => props.workspace.data);

    const workspaceName = computed(() => {
      return workspaceData.value?.displayName || workspaceData.value?.name || "";
    });

    const subscriptionDate = computed(() => {
      if (!workspaceData.value) {
        return "";
      }
      // Otherwise, show the 'since' date
      if (workspaceData.value?.since) {
        return getFormattedDateTime(workspaceData.value.since, "ll");
      }
      return "";
    });

    // Get a unique, translated, comma-separated list of subscription bundles
    const formattedBundles = computed(() => {
      const subscriptionBundles = workspaceData.value?.subscription?.bundles ?? [];
      if (!subscriptionBundles.length) {
        return "-";
      }
      // Use a Set to ensure bundles are unique before mapping
      const uniqueBundles = Array.from(new Set(subscriptionBundles));
      return uniqueBundles.map((bundle) => t(`subscriptions.bundles.${bundle}`)).join(", ");
    });

    // Get a translated, comma-separated list of selected modules
    const formattedModules = computed(() => {
      if (!workspaceData.value) {
        return "-";
      }
      // Use helper function to extract modules from the subscription object
      const modules = getEnabledModules(workspaceData.value.subscription);
      if (!modules.length) {
        return "-";
      }
      return modules
        .filter((module) => {
          // TODO: Remove crutch once SAT will be available in Canada
          if (isCanadaEnv) {
            return module !== SubscriptionModule.SECURITY_AWARENESS_TRAINING;
          }
          return true;
        })
        .map((module) => t(`subscriptions.modules.${module}`))
        .join(", ");
    });

    const bundleLabels = computed(() =>
      Object.fromEntries(
        Object.values(SubscriptionBundle).map((bundle) => [
          bundle,
          t(`subscriptions.bundles.${bundle}`),
        ])
      )
    );

    const columnLabels = computed(() => ({
      bundleName: t("mspWorkspacePreview.bundleBreakdown.columns.bundle"),
      regular: t("mspWorkspacePreview.bundleBreakdown.columns.regular"),
      total: t("mspWorkspacePreview.bundleBreakdown.columns.total"),
      channel: t("mspWorkspacePreview.bundleBreakdown.columns.channel"),
      child: t("mspWorkspacePreview.bundleBreakdown.columns.child"),
    }));

    const disabledActionsMap = computed(() => buildSingleHierarchyDisabledMap());

    const showViewWorkspace = computed(() => {
      return props.workspace.data?.subscriptionType !== SubscriptionType.ARCHIVED;
    });

    const executeWorkspaceAction = async (
      action: MspWorkspaceAction,
      item?: MspWorkspaceListItem
    ) => {
      if (!workspaceData.value) {
        return;
      }

      await handleMspWorkspaceViewAction(action, item ?? workspaceData.value);
    };

    const handleChildWorkspaceAction = async (
      action: MspWorkspaceAction,
      item?: MspWorkspaceListItem
    ) => {
      trackGtmEvent(
        GtmAction.MSP_TREEVIEW,
        `${GTMParams.CHILD_WORKSPACES_TABLE_CLICK} - ${startCase(action)}`
      );
      await executeWorkspaceAction(action, item);
    };

    const onOpenWorkspaceClick = async () => {
      trackGtmEvent(GtmAction.MSP_TREEVIEW, GTMParams.VIEW_WORKSPACE_CLICK);
      await executeWorkspaceAction(MspWorkspaceAction.VIEW_WORKSPACE);
    };

    // Build the array of workspace statistics for the cards
    const stats = computed<WorkspaceStat[]>(() => {
      if (!workspaceData.value) {
        return [];
      }

      return [
        {
          key: "tickets",
          label: t("mspWorkspacePreview.openTickets"),
          value: formatNumber(workspaceData.value.ticketsCount),
          icon: "$issueS",
        },
        {
          key: "devices",
          label: t("mspWorkspacePreview.protectedDevices"),
          value: formatNumber(workspaceData.value.protectedDevices),
          icon: "$deviceS",
        },
        {
          key: "users",
          label: t("mspWorkspacePreview.protectedUsers"),
          value: formatNumber(workspaceData.value.protectedUsers),
          icon: "$usersS",
        },
        {
          key: "protectable",
          label: t("mspWorkspacePreview.protectableUsers"),
          value: formatNumber(workspaceData.value.protectableUsers),
          icon: "$suspectedIdentityCompromiseS",
        },
      ];
    });

    const showTrialRemainingTime = computed(() => {
      if (!workspaceData.value) {
        return false;
      }
      return (
        workspaceData.value.subscriptionType === SubscriptionType.TRIAL &&
        !!workspaceData.value.trialEndDate
      );
    });

    const trialEndsNearExpiration = computed(() => {
      if (
        workspaceData.value?.subscriptionType !== SubscriptionType.TRIAL ||
        !workspaceData.value.trialEndDate
      ) {
        return false;
      }
      const inputDate = moment.utc(workspaceData.value.trialEndDate);
      return inputDate.isBefore(moment.utc().add(7, "days"));
    });

    const isTrialExpired = computed(() => {
      if (!workspaceData.value?.trialEndDate) {
        return false;
      }
      const currentTimestamp = moment.utc();
      const trialEndsTimestamp = moment.utc(workspaceData.value.trialEndDate);
      return trialEndsTimestamp.isBefore(currentTimestamp);
    });

    return {
      iconsMap,
      workspaceName,
      subscriptionDate,
      formattedBundles,
      formattedModules,
      stats,
      showTrialRemainingTime,
      getTimeUntil,
      trialEndsNearExpiration,
      isTrialExpired,
      onOpenWorkspaceClick,
      WorkspaceType,
      workspaceData,
      disabledActionsMap,
      ManageWorkspacesContext,
      showViewWorkspace,
      handleChildWorkspaceAction,
      bundleLabels,
      columnLabels,
      channelColumns,
    };
  },
});
</script>

<style scoped lang="scss">
.workspace-preview__header {
  position: sticky;
  top: 0;
  // 3, not 1: Vuetify gives a fixed table header z-index 2, so the child-workspaces table would
  // slide over this header the moment anyone adds fixed-header to it.
  z-index: 3;
  background-color: rgb(var(--v-theme-surface));

  // Scroll-driven animation rather than a scroll listener plus a class: the shadow is purely
  // presentational, so it stays in CSS and needs no component state. `scroll(nearest block)` binds
  // to .preview-wrapper, the closest scrolling ancestor, and the range fades the shadow in over
  // the first 24px instead of snapping it on. Browsers without scroll timelines show no shadow,
  // which is what we had before.
  animation: workspace-preview-header-shadow linear both;
  animation-timeline: scroll(nearest block);
  animation-range: 0 24px;
}

@keyframes workspace-preview-header-shadow {
  from {
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0);
  }

  to {
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.08);
  }
}

.branded-workspace-icon {
  padding: 4px;
  border-radius: 8px;

  .v-icon {
    &::before {
      color: rgb(var(--v-theme-white)) !important;
    }
  }
}
.workspace-preview__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.workspace-preview__meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
  margin-top: 12px;
}

.workspace-preview__subscription {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.workspace-preview__stat-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgb(var(--v-theme-indigo-pale));
  border-radius: 12px;
  padding: 12px 8px 12px 16px;
  gap: 8px;
  transition: background-color 0.5s;

  &--hoverable {
    &:hover {
      cursor: pointer;
      background-color: rgba(var(--v-theme-primary), var(--v-hover-opacity)) !important;
    }
  }
}

.workspace-preview__stat-value {
  font-family: Quicksand, sans-serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 600;
  line-height: 105%; /* 29.4px */
  letter-spacing: -0.3px;
}
</style>
